// ─────────────────────────────────────────────────────────────
// ResetPasswordScreen
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { SUPABASE_URL, SUPABASE_ANON_KEY, sb, authHeaders, ensureValidToken } from "../../lib/supabase";

export function ResetPasswordScreen({ tokenHash, supabaseClient, onComplete, onCancel }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verified, setVerified] = useState(false);

  // Step 1: Verify the token hash when component mounts
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/auth/v1/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            type: "recovery",
            token_hash: tokenHash
          })
        });

        const data = await res.json();

        if (res.ok && data?.access_token) {
          setVerified(true);
          // Store the session token for password update
          localStorage.setItem("_recovery_token", data.access_token);
        } else {
          setError("Invalid or expired reset link.");
        }
      } catch (e) {
        setError("Error verifying reset link: " + e.message);
      }
    };

    verifyToken();
  }, [tokenHash]);

  const handleReset = async () => {
    setError("");
    if (!newPassword || !confirmPassword) {
      setError("Please fill in both password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    try {
      const recoveryToken = localStorage.getItem("_recovery_token");
      
      if (!recoveryToken) {
        setError("Session expired. Please request a new reset link.");
        setLoading(false);
        return;
      }

      // Update password with the recovery session token
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": `Bearer ${recoveryToken}`
        },
        body: JSON.stringify({
          password: newPassword
        })
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.removeItem("_recovery_token");
        setSuccess(true);
        setTimeout(() => onComplete && onComplete(), 2000);
      } else {
        setError(data?.error_description || data?.error || "Failed to update password");
      }
    } catch (e) {
      console.error("Password update error:", e);
      setError("Connection error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <Card style={{ textAlign: "center", maxWidth: 420 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text }}>Password Reset!</h2>
          <p style={{ color: C.muted, marginTop: 12 }}>Redirecting to sign in...</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Card style={{ maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>Reset Your Password</h2>
          <p style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Enter your new password</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", display: "block", marginBottom: 6 }}>New Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleReset()}
                style={{ width: "100%", padding: "12px 14px", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, boxSizing: "border-box" }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer" }}>
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Confirm Password</label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleReset()}
              style={{ width: "100%", padding: "12px 14px", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, boxSizing: "border-box" }}
            />
          </div>

          {error && (
            <div style={{ background: C.red + "15", border: `1px solid ${C.red}30`, borderRadius: 8, padding: "10px 14px", color: "#ff6b6b", fontSize: 13 }}>
              ⚠️ {error}
            </div>
          )}

          <Btn onClick={handleReset} disabled={loading} style={{ width: "100%", padding: 14 }}>
            {loading ? "Resetting..." : "Reset Password →"}
          </Btn>

          <button onClick={onCancel} style={{ width: "100%", padding: 10, background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, textDecoration: "underline" }}>
            Back to Sign In
          </button>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PASSWORD RESET FORM (Forgot Password Flow)
// ─────────────────────────────────────────────────────────────
